import type { SyncrelStore } from "../data/syncrel-store";
import { deleteWorkspaceRoom, listWorkspaceRooms, saveSnapshot, type WorkspaceRoomRecord } from "../data/persistence";
import type { EntityStatus, EntityType, FieldType, FrameworkId, SyncrelState } from "../domain/types";
import { visibleFrameworkEntities } from "../domain/framework-scope";
import { enabledFrameworks, frameworkCatalog, frameworkTemplate } from "../domain/framework-manager";
import {
  accessModeFromUrl,
  createShareToken,
  createWorkspaceRoomId,
  shouldStartReadOnlyFromUrl,
  type ShareAccess,
  workspaceShareLink,
} from "../domain/share";
export {
  accessModeFromUrl,
  createShareToken,
  createWorkspaceRoomId,
  shouldStartReadOnlyFromUrl,
  workspaceShareLink,
} from "../domain/share";
import {
  appendWorkspaceImport,
  exportWorkspaceFile,
  importedWorkspaceRoom,
  parseWorkspaceFile,
  prepareImportedWorkspaceState,
  workspaceFileName,
  type WorkspaceExportFile,
} from "../domain/workspace-file";
import { renderFrameworkLibrary } from "./framework-library";
import { renderSpatialMap, CARD_ANCHOR_X, CARD_ANCHOR_Y, MAP_WORLD_HEIGHT, MAP_WORLD_WIDTH } from "./spatial-map";
import { renderSpecEditor } from "./spec-editor";
import { renderStructuralGrid, type GridSort } from "./structural-grid";
import { renderSettingsPanel } from "./dialogs";
import { escapeHtml, selectedEntity } from "./render-utils";
import { renderBrandMark } from "./brand";

let docTimer: number | null = null;
const DRAG_THRESHOLD = 3;
const SVG_NS = "http://www.w3.org/2000/svg";
const TUTORIAL_KEY = "syncrel:tutorial-seen";
const CHAT_AUTHOR_KEY = "syncrel:chat-author";
const SPEC_VIEWER_KEY = "syncrel:spec-viewer";
const MAP_ZOOM_MIN = 0.35;
const MAP_ZOOM_MAX = 2.5;
const MAP_ZOOM_STEP = 0.15;
const MAP_GRID_BASE_SIZE = 22;
const FRAMEWORK_TOPBAR_LIMIT = 3;

export type CanvasTool = "select" | "hand" | "node" | "connector" | "note";

const DEFAULT_CANVAS_TOOL: CanvasTool = "select";

// The app renders a static shell once, then repaints only the regions whose
// HTML actually changed. This replaces the previous full innerHTML rebuild on
// every keystroke/drag — the single largest source of UI lag.
export const mountApp = (root: HTMLElement, store: SyncrelStore): void => {
  root.innerHTML = renderShell();
  root.dataset.activeTool = DEFAULT_CANVAS_TOOL;
  root.dataset.accessMode = accessModeFromUrl(window.location.href, store.getState());
  let roomRecords: WorkspaceRoomRecord[] = [];
  let latestState: SyncrelState | null = null;
  let activeUserCount = store.activeUserCount();

  const region = (name: string): HTMLElement => {
    const el = root.querySelector<HTMLElement>(`[data-region="${name}"]`);
    if (!el) throw new Error(`Missing region: ${name}`);
    return el;
  };
  const regions = {
    commandBar: region("commandBar"),
    bottomToolbar: region("bottomToolbar"),
    frameworkWelcome: region("frameworkWelcome"),
    frameworks: region("frameworks"),
    map: region("map"),
    grid: region("grid"),
    spec: region("spec"),
    settings: region("settings"),
    chat: region("chat"),
  };
  const signature: Record<string, string> = {};

  const render = (state: SyncrelState) => {
    latestState = state;
    paint(regions.commandBar, "commandBar", renderCommandBar(state, roomRecords, activeUserCount, getAccessMode(root)), signature);
    paint(regions.bottomToolbar, "bottomToolbar", renderBottomToolbar(state, getActiveTool(root)), signature);
    paint(
      regions.frameworkWelcome,
      "frameworkWelcome",
      renderFrameworkWelcome(state, root.dataset.pendingFrameworkWelcome ?? null),
      signature,
    );
    paint(regions.frameworks, "frameworks", renderFrameworkLibrary(state), signature);
    paint(regions.map, "map", renderSpatialMap(state), signature);
    paint(regions.grid, "grid", renderStructuralGrid(state, getGridSort(root)), signature);
    paint(
      regions.spec,
      "spec",
      renderSpecEditor(state, isSpecOutlineExpanded(root), specViewerId(), root.dataset.accessMode !== "view"),
      signature,
    );
    paint(regions.settings, "settings", renderSettingsPanel(state), signature);
    paint(regions.chat, "chat", renderChatMessages(state), signature);
    applyMapZoom(root, getMapZoom(root));
    applyCanvasTool(root, getActiveTool(root));
    applyToolPanelState(root);
    applyChatPanelState(root);
  };

  const refreshRooms = (): void => {
    void listWorkspaceRooms().then((records) => {
      roomRecords = records;
      if (latestState) render(latestState);
    });
  };

  wireEvents(root, store, () => render(store.getState()), refreshRooms);
  const unsubscribePresence = store.subscribePresence((count) => {
    activeUserCount = count;
    if (latestState) render(latestState);
  });
  store.subscribe(render);
  refreshRooms();
  window.setTimeout(() => {
    if (localStorage.getItem(TUTORIAL_KEY) !== "1") openTutorial(root, 0);
  }, 120);

  window.addEventListener("beforeunload", () => {
    unsubscribePresence();
    store.destroy();
  }, { once: true });
};

// Only a *text-editing* control holding focus blocks a repaint, so a caret is
// never wiped mid-keystroke. Buttons, selects and checkboxes repaint freely.
const holdsTextFocus = (el: HTMLElement): boolean => {
  const active = document.activeElement;
  return (
    active instanceof HTMLElement &&
    el.contains(active) &&
    active.matches("textarea, input:not([type=checkbox]):not([type=radio])")
  );
};

const paint = (el: HTMLElement, key: string, html: string, signature: Record<string, string>): void => {
  if (signature[key] === html) return; // region unchanged — skip all DOM work
  if (holdsTextFocus(el)) return; // don't clobber the field being typed in
  signature[key] = html;
  el.innerHTML = html;
};

export const renderShell = (): string => `
  <main class="app-shell">
    <section class="main-stage">
      <header class="command-bar" data-region="commandBar"></header>
      ${renderFrameworkPopoverShell()}
      ${renderChatPopoverShell()}
      <div class="workbench">
        <div class="region region--map" data-region="map"></div>
        <div class="region region--framework-welcome" data-region="frameworkWelcome"></div>
        <div class="tool-panel-stack" data-tool-panel-stack>
          ${renderToolPanelShell("grid", "Fields")}
          ${renderToolPanelShell("settings", "Settings & Data")}
        </div>
        ${renderSpecPanelShell()}
      </div>
      <footer class="bottom-toolbar" data-region="bottomToolbar"></footer>
    </section>
  </main>
  <div class="command-palette" data-command-palette hidden>
    <div class="command-dialog" role="dialog" aria-modal="true" aria-label="Command palette">
      <input data-command-search placeholder="Jump, create, or run..." autocomplete="off" />
      <div class="command-list" data-command-list></div>
    </div>
  </div>
  <div class="share-modal" data-share-modal hidden>
    <section class="share-dialog" role="dialog" aria-modal="true" aria-label="Share workspace">
      <div class="share-dialog__head">
        <div>
          <strong>Share workspace</strong>
          <span>Edit or view access for this room</span>
        </div>
        <button type="button" class="tool-panel__close" data-close-share-modal title="Close Share" aria-label="Close Share">
          ${iconSvg("close")}
        </button>
      </div>
      <div class="share-links">
        <label class="share-link-row" data-share-edit-row hidden>
          <span>Edit link</span>
          <input data-share-edit-link readonly aria-label="Edit link" />
          <button type="button" data-copy-share-link="edit">Copy</button>
        </label>
        <label class="share-link-row">
          <span>View-only link</span>
          <input data-share-view-link readonly aria-label="View-only link" />
          <button type="button" data-copy-share-link="view">Copy</button>
        </label>
      </div>
      <p class="share-dialog__note">View-only links open the room without editing controls in this client.</p>
    </section>
  </div>
  <div class="workspace-import-modal" data-workspace-import-modal hidden>
    <section class="workspace-import-dialog" role="dialog" aria-modal="true" aria-label="Import workspace file">
      <div class="workspace-import-dialog__head">
        <div>
          <strong>Import workspace file</strong>
          <span data-workspace-import-summary>Choose how to place this file.</span>
        </div>
        <button type="button" class="tool-panel__close" data-close-workspace-import title="Close import" aria-label="Close import">
          ${iconSvg("close")}
        </button>
      </div>
      <div class="workspace-import-dialog__actions">
        <button type="button" data-workspace-import-action="append">
          <strong>Append to current workspace</strong>
          <span>Add cards, fields, links, specs, polls, and chat without overwriting existing room data.</span>
        </button>
        <button type="button" data-workspace-import-action="new">
          <strong>Create new workspace</strong>
          <span>Save this file as its own room with a fresh local workspace link.</span>
        </button>
      </div>
      <p class="workspace-import-dialog__note">Import never replaces an existing room automatically.</p>
    </section>
  </div>
  <div class="share-toast" data-share-toast role="status" aria-live="polite" hidden></div>
  <div class="tutorial-layer" data-tutorial-layer hidden>
    <div class="tutorial-spotlight" data-tutorial-spotlight></div>
    <section class="tutorial-card" data-tutorial-card role="dialog" aria-modal="false" aria-label="Syncrel tutorial"></section>
  </div>
`;

const renderChatPopoverShell = (): string => `
  <section class="chat-popover" data-chat-panel hidden>
    <div class="tool-panel__chrome">
      <strong>Room Chat</strong>
      <button
        type="button"
        class="tool-panel__close"
        data-close-chat
        title="Close Room Chat"
        aria-label="Close Room Chat"
      >
        ${iconSvg("close")}
      </button>
    </div>
    <div class="chat-messages" data-region="chat"></div>
    <form class="chat-form" data-chat-form>
      <input name="message" data-chat-input autocomplete="off" placeholder="Message this room" />
      <button type="submit" title="Send message" aria-label="Send message">${iconSvg("send")}</button>
    </form>
  </section>
`;

const renderFrameworkPopoverShell = (): string => `
  <section class="framework-popover" data-tool-panel="frameworks" hidden>
    <div class="tool-panel__chrome">
      <strong>Framework Library</strong>
      <button
        type="button"
        class="tool-panel__close"
        data-close-panel="frameworks"
        title="Close Framework Library"
        aria-label="Close Framework Library"
      >
        ${iconSvg("close")}
      </button>
    </div>
    <div class="region region--tool" data-region="frameworks"></div>
  </section>
`;

const renderToolPanelShell = (name: string, title: string): string => {
  const panelClass = name === "grid" ? "tool-panel tool-panel--grid" : "tool-panel";
  return `
  <section class="${panelClass}" data-tool-panel="${escapeHtml(name)}" hidden>
    <div class="tool-panel__chrome">
      <strong>${escapeHtml(title)}</strong>
      <button
        type="button"
        class="tool-panel__close"
        data-close-panel="${escapeHtml(name)}"
        title="Close ${escapeHtml(title)}"
        aria-label="Close ${escapeHtml(title)}"
      >
        ${iconSvg("close")}
      </button>
    </div>
    <div class="region region--tool" data-region="${escapeHtml(name)}"></div>
  </section>
`;
};

const renderSpecPanelShell = (): string => `
  <section class="tool-panel tool-panel--spec" data-tool-panel="spec" data-spec-dock hidden>
    <div class="tool-panel__chrome spec-dock__chrome">
      <div>
        <strong>Spec</strong>
      </div>
      <button
        type="button"
        class="tool-panel__close"
        data-close-panel="spec"
        title="Close Spec"
        aria-label="Close Spec"
      >
        ${iconSvg("close")}
      </button>
    </div>
    <div class="region region--tool" data-region="spec"></div>
  </section>
`;

export const renderCommandBar = (
  state: SyncrelState,
  rooms: WorkspaceRoomRecord[] = [],
  activeUserCount = 1,
  accessMode: ShareAccess = "edit",
): string => {
  const selected = state.workspace.selectedEntityId
    ? state.entities.find((entity) => entity.id === state.workspace.selectedEntityId) ?? null
    : null;
  const selectedRelation = state.relations.find((relation) => relation.id === state.workspace.selectedRelationId);
  const activeFramework = state.workspace.activeFramework ? frameworkTemplate(state.workspace.activeFramework) : null;
  const frameworkTabs = enabledFrameworks(state);
  const activeFrameworkIndex = activeFrameworkIndexFor(frameworkTabs, state.workspace.activeFramework);
  const visibleFrameworkTabs = visibleFrameworkTopbarTabs(frameworkTabs, activeFrameworkIndex);
  const hasPreviousFramework = activeFrameworkIndex > 0;
  const hasNextFramework = activeFrameworkIndex >= 0 && activeFrameworkIndex < frameworkTabs.length - 1;
  const roomOptions = workspaceRoomOptions(state, rooms);
  const activeUsers = Math.max(1, Math.round(activeUserCount));
  const activeUserText = `${activeUsers} active`;
  const activeUserTitle = `${activeUsers} active ${activeUsers === 1 ? "user" : "users"} in this workspace`;
  const isViewOnly = accessMode === "view";
  return `
    <div class="workspace-title" data-access-mode="${accessMode}">
      ${renderBrandMark("workspace-brand__mark")}
      <strong>Sync Rel</strong>
      <label class="room-switcher">
        <span class="sr-only">Room</span>
        <select data-room-switch aria-label="Switch room" title="Switch room">
          ${roomOptions.map((room) => `
            <option value="${escapeHtml(room.room)}" ${room.room === state.workspace.room ? "selected" : ""}>
              ${escapeHtml(roomOptionLabel(room))}
            </option>
          `).join("")}
        </select>
      </label>
      ${isViewOnly
        ? `<span class="access-mode-badge" data-view-only-badge>View only</span>`
        : `
          <button type="button" class="workspace-icon-command" data-rename-workspace title="Rename workspace" aria-label="Rename workspace">${iconSvg("rename")}</button>
          <button type="button" class="workspace-icon-command" data-new-workspace title="New room" aria-label="New room">${iconSvg("new")}</button>
          <button type="button" class="workspace-icon-command workspace-icon-command--danger" data-delete-workspace title="Delete room" aria-label="Delete room">${iconSvg("trash")}</button>
        `}
    </div>
    <nav class="framework-topbar" data-framework-bar aria-label="Framework views">
      <button
        type="button"
        class="framework-topbar__nav"
        data-framework-nav="prev"
        title="Previous framework"
        aria-label="Previous framework"
        ${hasPreviousFramework ? "" : "disabled"}
      >
        ${iconSvg("chevronLeft")}
      </button>
      <div
        class="framework-topbar__tabs"
        role="tablist"
        aria-label="Visible framework views"
        style="--visible-framework-count: ${Math.max(visibleFrameworkTabs.length, 1)}"
        data-visible-framework-count="${visibleFrameworkTabs.length}"
        data-total-framework-count="${frameworkTabs.length}"
      >
        ${visibleFrameworkTabs.map((framework) => `
          <button
            type="button"
            class="framework-topbar__tab ${framework.id === state.workspace.activeFramework ? "is-active" : ""}"
            data-framework="${escapeHtml(framework.id)}"
            role="tab"
            aria-selected="${framework.id === state.workspace.activeFramework ? "true" : "false"}"
            title="${escapeHtml(framework.label)}"
          >
            ${escapeHtml(framework.shortLabel)}
          </button>
        `).join("")}
      </div>
      <button
        type="button"
        class="framework-topbar__nav"
        data-framework-nav="next"
        title="Next framework"
        aria-label="Next framework"
        ${hasNextFramework ? "" : "disabled"}
      >
        ${iconSvg("chevronRight")}
      </button>
      <button
        type="button"
        class="framework-topbar__manage"
        data-tool-toggle="frameworks"
        aria-expanded="false"
        title="Show all frameworks"
        aria-label="Show all frameworks"
      >
        ${iconSvg("frameworks")}
      </button>
    </nav>
    <div class="selection-readout">
      <span>${escapeHtml(activeFramework?.label ?? selectedRelation?.type ?? selected?.type ?? "base")}</span>
      <strong>${escapeHtml(selectedRelation?.label ?? selected?.name ?? "No selection")}</strong>
      <div class="command-actions">
        <span
          class="active-users-indicator"
          data-active-users="${activeUsers}"
          title="${escapeHtml(activeUserTitle)}"
          aria-label="${escapeHtml(activeUserTitle)}"
        >
          <span aria-hidden="true"></span>${escapeHtml(activeUserText)}
        </span>
        <button type="button" data-toggle-chat aria-expanded="false" title="Room chat - ${escapeHtml(activeUserText)}" aria-label="Room chat - ${escapeHtml(activeUserText)}">
          ${iconSvg("chat")}
          ${(state.chat?.length ?? 0) > 0 ? `<span class="command-badge">${Math.min(state.chat?.length ?? 0, 99)}</span>` : ""}
        </button>
        <button type="button" class="share-command" data-share-workspace title="Share workspace" aria-label="Share workspace">${iconSvg("share")}<span>Share</span></button>
        <button type="button" data-start-tutorial title="Start tutorial" aria-label="Start tutorial">${iconSvg("tutorial")}</button>
        <button type="button" data-open-command title="Command palette" aria-label="Command palette">${iconSvg("command")}</button>
      </div>
    </div>
  `;
};

export const renderChatMessages = (state: SyncrelState): string => {
  const messages = state.chat ?? [];
  return messages.length === 0
    ? `<div class="chat-empty">No messages yet.</div>`
    : messages.map((message) => `
      <article class="chat-message">
        <div>
          <strong>${escapeHtml(message.author)}</strong>
          <time datetime="${new Date(message.createdAt).toISOString()}">${escapeHtml(formatChatTime(message.createdAt))}</time>
        </div>
        <p>${escapeHtml(message.text)}</p>
      </article>
    `).join("");
};

const formatChatTime = (createdAt: number): string => (
  new Date(createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
);

const workspaceRoomOptions = (state: SyncrelState, rooms: WorkspaceRoomRecord[]): WorkspaceRoomRecord[] => {
  const current: WorkspaceRoomRecord = {
    room: state.workspace.room,
    name: state.workspace.name,
    savedAt: state.workspace.updatedAt,
  };
  const byRoom = new Map<string, WorkspaceRoomRecord>([[current.room, current]]);
  for (const room of [...rooms].sort((a, b) => b.savedAt - a.savedAt)) {
    if (!byRoom.has(room.room)) byRoom.set(room.room, room);
  }
  return Array.from(byRoom.values());
};

const roomOptionLabel = (room: WorkspaceRoomRecord): string => {
  const cleanName = room.name.trim();
  if (!cleanName || cleanName === room.room) return room.room;
  return `${cleanName} · ${room.room}`;
};

const activeFrameworkIndexFor = (
  frameworkTabs: ReturnType<typeof enabledFrameworks>,
  activeFramework: FrameworkId | null,
): number => {
  if (frameworkTabs.length === 0) return -1;
  const activeIndex = frameworkTabs.findIndex((framework) => framework.id === activeFramework);
  return activeIndex >= 0 ? activeIndex : 0;
};

const visibleFrameworkTopbarTabs = (
  frameworkTabs: ReturnType<typeof enabledFrameworks>,
  activeIndex: number,
): ReturnType<typeof enabledFrameworks> => {
  if (frameworkTabs.length <= FRAMEWORK_TOPBAR_LIMIT) return frameworkTabs;
  const safeActiveIndex = activeIndex >= 0 ? activeIndex : 0;
  const start = Math.min(
    Math.max(safeActiveIndex - 1, 0),
    Math.max(frameworkTabs.length - FRAMEWORK_TOPBAR_LIMIT, 0),
  );
  return frameworkTabs.slice(start, start + FRAMEWORK_TOPBAR_LIMIT);
};

const adjacentFrameworkId = (state: SyncrelState, direction: -1 | 1): FrameworkId | null => {
  const frameworkTabs = enabledFrameworks(state);
  const activeIndex = activeFrameworkIndexFor(frameworkTabs, state.workspace.activeFramework);
  const next = frameworkTabs[activeIndex + direction];
  return next?.id ?? null;
};

export const renderFrameworkWelcome = (
  state: SyncrelState,
  pendingFrameworkId: string | null = null,
): string => {
  const enabled = enabledFrameworks(state);
  const activeId = state.workspace.activeFramework ?? enabled[0]?.id ?? null;
  if (!activeId || activeId !== pendingFrameworkId) return "";

  const framework = frameworkTemplate(activeId);
  const fieldLabels = framework.fields
    .filter((field) => !field.readonly)
    .slice(0, 4)
    .map((field) => field.label);

  return `
    <section
      class="framework-welcome"
      data-framework-welcome="${escapeHtml(framework.id)}"
      role="dialog"
      aria-modal="false"
      aria-label="${escapeHtml(framework.label)} welcome"
    >
      <button
        type="button"
        class="framework-welcome__close"
        data-close-framework-welcome="${escapeHtml(framework.id)}"
        title="Close ${escapeHtml(framework.label)} welcome"
        aria-label="Close ${escapeHtml(framework.label)} welcome"
      >
        ${iconSvg("close")}
      </button>
      <span>Framework</span>
      <h2>${escapeHtml(framework.label)}</h2>
      <p>${escapeHtml(framework.description)}</p>
      ${fieldLabels.length > 0 ? `
        <div class="framework-welcome__fields" aria-label="${escapeHtml(framework.label)} starter fields">
          ${fieldLabels.map((label) => `<span>${escapeHtml(label)}</span>`).join("")}
        </div>
      ` : ""}
    </section>
  `;
};

const prepareFrameworkWelcome = (root: HTMLElement, store: SyncrelStore, frameworkId: FrameworkId): void => {
  const state = store.getState();
  const enabledIds = state.workspace.enabledFrameworks?.length
    ? state.workspace.enabledFrameworks
    : state.workspace.activeFramework
      ? [state.workspace.activeFramework]
      : [];
  const wasAlreadyEnabled = enabledIds.includes(frameworkId);
  const wasAlreadySeen = state.workspace.frameworkWelcomeSeen?.includes(frameworkId) ?? false;

  if (wasAlreadyEnabled || wasAlreadySeen) {
    clearPendingFrameworkWelcome(root);
    return;
  }

  root.dataset.pendingFrameworkWelcome = frameworkId;
  store.markFrameworkWelcomeSeen(frameworkId);
};

const clearPendingFrameworkWelcome = (root: HTMLElement): void => {
  delete root.dataset.pendingFrameworkWelcome;
};

export const renderBottomToolbar = (state: SyncrelState, activeTool: CanvasTool = DEFAULT_CANVAS_TOOL): string => {
  const selected = state.workspace.selectedEntityId
    ? state.entities.find((entity) => entity.id === state.workspace.selectedEntityId) ?? null
    : null;
  return `
    <div class="bottom-toolbar__primary" aria-label="Canvas tools">
      ${renderToolModeButton("select", "Select", "Pick and edit cards", activeTool)}
      ${renderToolModeButton("hand", "Hand", "Pan the canvas", activeTool)}
      ${renderToolModeButton("node", "Node", "Click the canvas to place nodes repeatedly", activeTool, true)}
      ${renderToolModeButton("connector", "Link", "Drag from one card to another", activeTool)}
      ${renderToolModeButton("note", "Note", "Click the canvas to place notes", activeTool)}
      ${renderDrawerButton("grid", "Fields", "Open field schema and rows", "field", String(state.schema.fields.length))}
      ${renderDrawerButton("spec", "Spec", "Open selected card spec document", "spec", selected?.status ? selected.status.slice(0, 1).toUpperCase() : "-")}
      ${renderDrawerButton("settings", "More", "Open settings and data tools", "settings", "Data")}
    </div>
    <div class="bottom-toolbar__utility">
      <div class="zoom-controls" aria-label="Canvas zoom controls">
        <button type="button" class="icon-command" data-zoom-out title="Zoom out" aria-label="Zoom out">${iconSvg("zoomOut")}</button>
        <button type="button" class="icon-command zoom-value" data-zoom-reset title="Reset zoom" aria-label="Reset zoom"><span data-zoom-value>100%</span></button>
        <button type="button" class="icon-command" data-zoom-in title="Zoom in" aria-label="Zoom in">${iconSvg("zoomIn")}</button>
      </div>
      <button type="button" class="icon-command" data-undo title="Undo" aria-label="Undo">${iconSvg("undo")}</button>
      <button type="button" class="icon-command" data-redo title="Redo" aria-label="Redo">${iconSvg("redo")}</button>
    </div>
  `;
};

const renderToolModeButton = (
  tool: CanvasTool,
  label: string,
  title: string,
  activeTool: CanvasTool,
  createsEntity = false,
): string => `
  <button
    type="button"
    class="bottom-tool bottom-tool--mode ${activeTool === tool ? "is-active" : ""}"
    data-tool-mode="${tool}"
    data-tool-icon="${tool}"
    ${createsEntity ? "data-add-entity" : ""}
    aria-pressed="${activeTool === tool ? "true" : "false"}"
    title="${escapeHtml(title)}"
    aria-label="${escapeHtml(title)}"
  >
    ${iconSvg(tool)}
    <span class="sr-only">${escapeHtml(label)}</span>
  </button>
`;

const renderDrawerButton = (
  name: string,
  label: string,
  title: string,
  icon: ToolbarIcon,
  badge: string,
): string => `
  <button
    type="button"
    class="bottom-tool bottom-tool--drawer"
    data-tool-toggle="${escapeHtml(name)}"
    data-tool-icon="${escapeHtml(name)}"
    aria-expanded="false"
    title="${escapeHtml(title)}"
    aria-label="${escapeHtml(title)}"
  >
    ${iconSvg(icon)}
    <span class="sr-only">${escapeHtml(label)}</span>
    <span class="tool-badge">${escapeHtml(badge)}</span>
  </button>
`;

type ToolbarIcon =
  | CanvasTool
  | "field"
  | "spec"
  | "settings"
  | "chat"
  | "new"
  | "rename"
  | "share"
  | "trash"
  | "send"
  | "undo"
  | "redo"
  | "command"
  | "tutorial"
  | "zoomIn"
  | "zoomOut"
  | "frameworks"
  | "chevronLeft"
  | "chevronRight"
  | "close";

const iconSvg = (icon: ToolbarIcon): string => {
  const common = `width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"`;
  const paths: Record<ToolbarIcon, string> = {
    select: `<path d="M5 3l12 9-5 1.5 3.5 6-2.8 1.5-3.4-6L5 19V3z" />`,
    hand: `<path d="M7 12V6.5a1.5 1.5 0 013 0V12" /><path d="M10 11V5.5a1.5 1.5 0 013 0V12" /><path d="M13 12V7.5a1.5 1.5 0 013 0V14" /><path d="M16 13.5l.7-.7a1.7 1.7 0 012.4 2.4l-3.2 3.2A6 6 0 0111.7 20H11a6 6 0 01-6-6v-2a1.5 1.5 0 013 0v1" />`,
    node: `<rect x="5" y="5" width="14" height="14" rx="2" /><path d="M9 12h6" /><path d="M12 9v6" />`,
    connector: `<circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="12" r="2.5" /><path d="M8.5 12h7" />`,
    note: `<path d="M6 4h9l3 3v13H6V4z" /><path d="M15 4v4h4" /><path d="M9 12h6" /><path d="M9 16h4" />`,
    field: `<rect x="4" y="5" width="16" height="14" rx="1" /><path d="M4 10h16" /><path d="M9 5v14" /><path d="M15 5v14" />`,
    spec: `<path d="M6 4h8l4 4v12H6V4z" /><path d="M14 4v5h4" /><path d="M9 13h6" /><path d="M9 17h5" />`,
    settings: `<path d="M5 7h14" /><path d="M5 12h14" /><path d="M5 17h14" /><circle cx="9" cy="7" r="1.6" fill="currentColor" stroke="none" /><circle cx="15" cy="12" r="1.6" fill="currentColor" stroke="none" /><circle cx="11" cy="17" r="1.6" fill="currentColor" stroke="none" />`,
    chat: `<path d="M5 6h14v10H9l-4 4V6z" /><path d="M9 10h6" /><path d="M9 13h4" />`,
    new: `<path d="M12 5v14" /><path d="M5 12h14" />`,
    rename: `<path d="M5 19l4.5-1 9-9a2.1 2.1 0 00-3-3l-9 9L5 19z" /><path d="M13.5 6.5l4 4" /><path d="M5 21h14" />`,
    share: `<path d="M9 15l6-6" /><path d="M10 8h6v6" /><path d="M5 6v13h13" />`,
    trash: `<path d="M5 7h14" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M8 7l1-3h6l1 3" /><path d="M7 7l1 13h8l1-13" />`,
    send: `<path d="M4 12l16-7-7 16-2-7-7-2z" /><path d="M11 14l4-4" />`,
    undo: `<path d="M9 8H4v5" /><path d="M4 8l5 5" /><path d="M20 17a7 7 0 00-11-5" />`,
    redo: `<path d="M15 8h5v5" /><path d="M20 8l-5 5" /><path d="M4 17a7 7 0 0111-5" />`,
    command: `<circle cx="11" cy="11" r="5.5" /><path d="M16 16l3.5 3.5" />`,
    tutorial: `<path d="M9.5 8a3 3 0 115 2.3c-1.3.8-2 1.5-2 3.2" /><path d="M12.5 18h.01" />`,
    zoomIn: `<circle cx="10.5" cy="10.5" r="5.5" /><path d="M10.5 8v5" /><path d="M8 10.5h5" /><path d="M15 15l4 4" />`,
    zoomOut: `<circle cx="10.5" cy="10.5" r="5.5" /><path d="M8 10.5h5" /><path d="M15 15l4 4" />`,
    frameworks: `<path d="M5 6h14" /><path d="M5 12h14" /><path d="M5 18h14" /><path d="M12 4v16" />`,
    chevronLeft: `<path d="M15 6l-6 6 6 6" />`,
    chevronRight: `<path d="M9 6l6 6-6 6" />`,
    close: `<path d="M6 6l12 12" /><path d="M18 6L6 18" />`,
  };
  return `<svg ${common}>${paths[icon]}</svg>`;
};

// ---------------------------------------------------------------------------
// Event delegation: listeners are attached once to the root and survive region
// repaints, so we never re-bind handlers after a render.
// ---------------------------------------------------------------------------

const wireEvents = (
  root: HTMLElement,
  store: SyncrelStore,
  requestRender: () => void,
  refreshRooms: () => void,
): void => {
  let pendingWorkspaceImport: WorkspaceExportFile | null = null;

  root.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest("[data-command-palette]") === target) {
      closeCommandPalette(root);
      return;
    }

    if (target.closest("[data-share-modal]") === target) {
      closeShareModal(root);
      return;
    }

    if (target.closest("[data-workspace-import-modal]") === target) {
      closeWorkspaceImportModal(root);
      pendingWorkspaceImport = null;
      return;
    }

    if (target.closest("[data-close-share-modal]")) {
      closeShareModal(root);
      return;
    }

    if (target.closest("[data-close-workspace-import]")) {
      closeWorkspaceImportModal(root);
      pendingWorkspaceImport = null;
      return;
    }

    const workspaceImportAction = target.closest<HTMLElement>("[data-workspace-import-action]");
    if (workspaceImportAction) {
      void applyWorkspaceImportChoice(
        root,
        store,
        workspaceImportAction.dataset.workspaceImportAction === "new" ? "new" : "append",
        pendingWorkspaceImport,
        refreshRooms,
      );
      pendingWorkspaceImport = null;
      return;
    }

    const copyShare = target.closest<HTMLElement>("[data-copy-share-link]");
    if (copyShare) {
      copyShareLink(root, store, copyShare.dataset.copyShareLink === "view" ? "view" : "edit");
      return;
    }

    const command = target.closest<HTMLElement>("[data-command-action]");
    if (command) {
      executeCommand(root, store, command.dataset.commandAction ?? "");
      return;
    }

    const toolMode = target.closest<HTMLElement>("[data-tool-mode]");
    if (toolMode) {
      setActiveTool(root, asCanvasTool(toolMode.dataset.toolMode));
      return;
    }

    const closePanel = target.closest<HTMLElement>("[data-close-panel]");
    if (closePanel) {
      setToolPanel(root, closePanel.dataset.closePanel ?? "", false);
      return;
    }

    const closeFrameworkWelcome = target.closest<HTMLElement>("[data-close-framework-welcome]");
    if (closeFrameworkWelcome) {
      clearPendingFrameworkWelcome(root);
      const welcomeRegion = root.querySelector<HTMLElement>('[data-region="frameworkWelcome"]');
      if (welcomeRegion) welcomeRegion.innerHTML = "";
      return;
    }

    if (target.closest("[data-close-chat]")) {
      closeChatPanel(root);
      return;
    }

    if (target.closest("[data-toggle-chat]")) {
      toggleChatPanel(root);
      return;
    }

    if (target.closest("[data-close-inspector]")) {
      store.select(null);
      return;
    }

    const openSpecButton = target.closest<HTMLElement>("[data-open-spec]");
    if (openSpecButton) {
      const id = openSpecButton.dataset.openSpec || store.getState().workspace.selectedEntityId;
      if (id && id !== store.getState().workspace.selectedEntityId) store.select(id);
      openToolPanel(root, "spec");
      focusSpecEditor(root, id ?? undefined);
      return;
    }

    const toolToggle = target.closest<HTMLElement>("[data-tool-toggle]");
    if (toolToggle) {
      toggleToolPanel(root, toolToggle.dataset.toolToggle ?? "");
      if (toolToggle.dataset.toolToggle === "spec") focusSpecEditor(root);
      return;
    }

    const specInsert = target.closest<HTMLElement>("[data-spec-insert]");
    if (specInsert) {
      insertSpecSnippet(root, store, specInsert.dataset.specInsert ?? "");
      return;
    }

    if (target.closest("[data-toggle-spec-outline]")) {
      toggleSpecOutline(root);
      return;
    }

    const addSpecPoll = target.closest<HTMLElement>("[data-add-spec-poll]");
    if (addSpecPoll) {
      blurActiveTextControl();
      const entityId = addSpecPoll.dataset.addSpecPoll || store.getState().workspace.selectedEntityId;
      if (entityId) store.addSpecPoll(entityId);
      return;
    }

    const removeSpecPoll = target.closest<HTMLElement>("[data-remove-spec-poll]");
    if (removeSpecPoll) {
      blurActiveTextControl();
      store.removeSpecPoll(removeSpecPoll.dataset.specPollEntity ?? "", removeSpecPoll.dataset.removeSpecPoll ?? "");
      return;
    }

    const addSpecPollOption = target.closest<HTMLElement>("[data-add-spec-poll-option]");
    if (addSpecPollOption) {
      blurActiveTextControl();
      store.addSpecPollOption(
        addSpecPollOption.dataset.specPollEntity ?? "",
        addSpecPollOption.dataset.addSpecPollOption ?? "",
      );
      return;
    }

    const removeSpecPollOption = target.closest<HTMLElement>("[data-remove-spec-poll-option]");
    if (removeSpecPollOption) {
      blurActiveTextControl();
      store.removeSpecPollOption(
        removeSpecPollOption.dataset.specPollEntity ?? "",
        removeSpecPollOption.dataset.removeSpecPollOption ?? "",
        removeSpecPollOption.dataset.specPollOption ?? "",
      );
      return;
    }

    const specPollVote = target.closest<HTMLElement>("[data-spec-poll-vote]");
    if (specPollVote) {
      blurActiveTextControl();
      store.voteSpecPoll(
        specPollVote.dataset.specPollEntity ?? "",
        specPollVote.dataset.specPollVote ?? "",
        specPollVote.dataset.specPollOption ?? "",
        specViewerId(),
      );
      return;
    }

    if (target.closest("[data-new-workspace]")) {
      createWorkspace();
      return;
    }

    if (target.closest("[data-rename-workspace]")) {
      renameCurrentWorkspace(store, refreshRooms);
      return;
    }

    if (target.closest("[data-delete-workspace]")) {
      void deleteCurrentWorkspace(store, refreshRooms);
      return;
    }

    if (target.closest("[data-zoom-in]")) {
      zoomBy(root, MAP_ZOOM_STEP);
      return;
    }

    if (target.closest("[data-zoom-out]")) {
      zoomBy(root, -MAP_ZOOM_STEP);
      return;
    }

    if (target.closest("[data-zoom-reset]")) {
      setMapZoom(root, 1);
      return;
    }

    if (target.closest("[data-start-tutorial]")) {
      openTutorial(root, 0);
      return;
    }

    if (target.closest("[data-open-settings]")) {
      openToolPanel(root, "settings");
      return;
    }

    const tutorialAction = target.closest<HTMLElement>("[data-tutorial-action]");
    if (tutorialAction) {
      handleTutorialAction(root, tutorialAction.dataset.tutorialAction ?? "");
      return;
    }

    if (target.closest("[data-open-command]")) {
      openCommandPalette(root, store);
      return;
    }

    if (target.closest("[data-share-workspace]")) {
      openShareModal(root, store);
      return;
    }

    if (target.closest("[data-undo]")) {
      store.undo();
      return;
    }

    if (target.closest("[data-redo]")) {
      store.redo();
      return;
    }

    const deleteRelation = target.closest<HTMLElement>("[data-delete-relation]");
    if (deleteRelation) {
      store.removeRelation(deleteRelation.dataset.deleteRelation ?? "");
      return;
    }

    if (target.closest("[data-delete-checked-entities]")) {
      for (const entityId of checkedGridEntityIds(root)) {
        store.removeEntity(entityId);
      }
      return;
    }

    const gridSort = target.closest<HTMLElement>("button[data-grid-sort-key]");
    if (gridSort) {
      setGridSort(root, gridSort.dataset.gridSortKey ?? "");
      requestRender();
      return;
    }

    const relationHit = target.closest<SVGElement>("[data-relation-id]");
    if (relationHit) {
      store.selectRelation(relationHit.dataset.relationId ?? null);
      return;
    }

    const deleteEntity = target.closest<HTMLElement>("[data-delete-entity]");
    if (deleteEntity) {
      store.removeEntity(deleteEntity.dataset.deleteEntity ?? "");
      return;
    }

    const quickCreate = target.closest<HTMLElement>("[data-quick-create]");
    if (quickCreate) {
      createLinkedEntity(root, store, quickCreate.dataset.quickSource ?? "", quickCreate.dataset.quickCreate ?? "right");
      return;
    }

    if (target.closest("[data-add-entity]")) {
      store.addEntity({ position: nextEntityPosition(store.getState()) });
      focusSelectedEntity(root, store);
      return;
    }

    const frameworkNav = target.closest<HTMLElement>("[data-framework-nav]");
    if (frameworkNav) {
      const direction = frameworkNav.dataset.frameworkNav === "prev" ? -1 : 1;
      const frameworkId = adjacentFrameworkId(store.getState(), direction);
      if (frameworkId) {
        clearPendingFrameworkWelcome(root);
        store.applyFramework(frameworkId);
      }
      return;
    }

    const addFramework = target.closest<HTMLElement>("[data-add-framework]");
    if (addFramework) {
      const frameworkId = addFramework.dataset.addFramework as FrameworkId;
      prepareFrameworkWelcome(root, store, frameworkId);
      store.enableFramework(frameworkId);
      return;
    }

    const removeFramework = target.closest<HTMLElement>("[data-remove-framework]");
    if (removeFramework) {
      clearPendingFrameworkWelcome(root);
      store.removeFramework(removeFramework.dataset.removeFramework as FrameworkId);
      return;
    }

    const framework = target.closest<HTMLElement>("[data-framework]");
    if (framework) {
      clearPendingFrameworkWelcome(root);
      store.applyFramework(framework.dataset.framework as FrameworkId);
      return;
    }

    if (target.closest("[data-export-workspace]")) {
      exportCurrentWorkspace(root, store);
      return;
    }

    const row = target.closest<HTMLElement>("[data-row-id]");
    if (row && !target.closest("input, select, textarea, button, label")) {
      const id = row.dataset.rowId ?? null;
      if (id && id !== store.getState().workspace.selectedEntityId) store.select(id);
      return;
    }

    const surface = target.closest<HTMLElement>("[data-map-surface]");
    const activeTool = getActiveTool(root);
    if (
      surface &&
      event instanceof MouseEvent &&
      (activeTool === "node" || activeTool === "note") &&
      !target.closest("[data-card-id], [data-relation-id], input, textarea, select, button, form")
    ) {
      if (surface.dataset.justPanned === "1") return;
      const point = mapPoint(surface, event);
      createEntityAt(root, store, {
        x: point.x - CARD_ANCHOR_X,
        y: point.y - CARD_ANCHOR_Y,
      }, activeTool);
      return;
    }

    if (
      surface &&
      event instanceof MouseEvent &&
      !target.closest("[data-card-id], [data-relation-id], input, textarea, select, button, form")
    ) {
      const effect = emptyCanvasClickEffect(root.dataset.openToolPanel, surface.dataset.justPanned === "1");
      if (effect.closePanels) closeToolPanels(root);
      if (effect.clearSelection) store.select(null);
    }
  });

  root.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target instanceof HTMLInputElement && target.dataset.importWorkspaceFile !== undefined) {
      void handleWorkspaceImportFile(root, target, (file) => {
        pendingWorkspaceImport = file;
      });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.relationLabel !== undefined) {
      store.patchRelation(target.dataset.relationLabel, { label: target.value });
      return;
    }

    if (target instanceof HTMLSelectElement && target.dataset.roomSwitch !== undefined) {
      switchWorkspace(target.value);
      return;
    }

    if (target instanceof HTMLSelectElement && target.dataset.relationType !== undefined) {
      store.patchRelation(target.dataset.relationType, { type: target.value });
      return;
    }

    if (target instanceof HTMLTextAreaElement && target.dataset.specDoc !== undefined) {
      if (docTimer) {
        window.clearTimeout(docTimer);
        docTimer = null;
      }
      store.patchEntity(target.dataset.specDoc, { doc: target.value });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.gridName !== undefined) {
      store.patchEntity(target.dataset.gridName, { name: target.value });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.exportCheckAll !== undefined) {
      for (const input of root.querySelectorAll<HTMLInputElement>("[data-export-check]")) {
        input.checked = target.checked;
      }
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.specTitle !== undefined) {
      store.patchEntity(target.dataset.specTitle, { name: target.value });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.inlineTitle !== undefined) {
      store.patchEntity(target.dataset.inlineTitle, { name: target.value });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.inspectorName !== undefined) {
      store.patchEntity(target.dataset.inspectorName, { name: target.value });
      return;
    }

    if (target instanceof HTMLSelectElement && target.dataset.inspectorType !== undefined) {
      store.patchEntity(target.dataset.inspectorType, { type: target.value as EntityType });
      return;
    }

    if (target instanceof HTMLSelectElement && target.dataset.inspectorStatus !== undefined) {
      store.patchEntity(target.dataset.inspectorStatus, { status: target.value as EntityStatus });
      return;
    }

    if (
      (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) &&
      target.dataset.inspectorFieldKey !== undefined
    ) {
      const entityId = target.dataset.inspectorFieldEntity ?? "";
      const key = target.dataset.inspectorFieldKey ?? "";
      const value = target instanceof HTMLInputElement && target.type === "checkbox"
        ? target.checked
        : coerceValue(target.value);
      store.patchEntity(entityId, { fields: { [key]: value } });
      return;
    }

    if (
      (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) &&
      target.dataset.fieldKey !== undefined
    ) {
      const entityId = target.dataset.fieldEntity ?? "";
      const key = target.dataset.fieldKey ?? "";
      const value = target instanceof HTMLInputElement && target.type === "checkbox"
        ? target.checked
        : coerceValue(target.value);
      if (key === "status") {
        const next = store.getState();
        store.setState({
          ...next,
          entities: next.entities.map((entity) => (
            entity.id === entityId ? { ...entity, status: String(value) as typeof entity.status } : entity
          )),
        });
        return;
      }
      store.patchEntity(entityId, { fields: { [key]: value } });
    }
  });

  root.addEventListener("input", (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.dataset.commandSearch !== undefined) {
      renderCommandItems(root, store, target.value);
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.inlineTitle !== undefined) {
      store.patchEntity(target.dataset.inlineTitle, { name: target.value });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.inspectorName !== undefined) {
      store.patchEntity(target.dataset.inspectorName, { name: target.value });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.specPollQuestion !== undefined) {
      store.patchSpecPollQuestion(
        target.dataset.specPollEntity ?? "",
        target.dataset.specPollQuestion,
        target.value,
      );
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.specPollOptionLabel !== undefined) {
      store.patchSpecPollOption(
        target.dataset.specPollEntity ?? "",
        target.dataset.specPollOptionLabel,
        target.dataset.specPollOption ?? "",
        target.value,
      );
      updateSpecPollOptionDisplay(root, target.dataset.specPollOption ?? "", target.value);
      return;
    }

    if (target instanceof HTMLTextAreaElement && target.dataset.specDoc !== undefined) {
      const id = target.dataset.specDoc;
      const value = target.value;
      if (docTimer) window.clearTimeout(docTimer);
      docTimer = window.setTimeout(() => store.patchEntity(id, { doc: value }), 220);
      return;
    }
  });

  root.addEventListener("wheel", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const surface = target.closest<HTMLElement>("[data-map-surface]");
    if (!surface || target.closest("input, textarea, select, button")) return;
    event.preventDefault();
    const current = getMapZoom(root);
    const next = current * Math.exp(-event.deltaY * 0.0018);
    setMapZoom(root, next, { clientX: event.clientX, clientY: event.clientY });
  }, { passive: false });

  root.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches("[data-schema-form]")) {
      event.preventDefault();
      const data = new FormData(form);
      const label = String(data.get("label") ?? "").trim();
      if (!label) return;
      const type = asFieldType(String(data.get("type") ?? "text"));
      const options = String(data.get("options") ?? "")
        .split(",")
        .map((option) => option.trim())
        .filter(Boolean);
      const defaultRaw = String(data.get("defaultValue") ?? "").trim();
      store.addSchemaField({
        label,
        type,
        options,
        defaultValue: defaultRaw || undefined,
        required: data.has("required"),
        readonly: data.has("readonly") || type === "computed",
      });
      form.reset();
      return;
    }

    if (form.matches("[data-chat-form]")) {
      event.preventDefault();
      const input = form.querySelector<HTMLInputElement>("[data-chat-input]");
      const text = input?.value ?? "";
      if (!text.trim()) return;
      store.addChatMessage({ author: chatAuthorName(), text });
      form.reset();
      openChatPanel(root);
      window.setTimeout(() => focusChatInput(root), 0);
      return;
    }

    if (form.matches("[data-relation-form]")) {
      event.preventDefault();
      return;
    }

    if (form.matches("[data-connect-form]")) {
      event.preventDefault();
      const selected = selectedEntity(store.getState());
      if (!selected) return;
      const data = new FormData(form);
      const targetId = String(data.get("targetId") ?? "");
      const type = String(data.get("type") ?? "drives");
      if (!targetId || targetId === selected.id) return;
      store.linkEntities({
        fromId: selected.id,
        toId: targetId,
        type,
        label: type === "blocked-by" ? "blocked by" : type,
      });
    }
  });

  root.addEventListener("dblclick", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const surface = target.closest<HTMLElement>("[data-map-surface]");
    if (getActiveTool(root) !== "select") return;
    if (!surface || target.closest("[data-card-id], [data-relation-id], input, textarea, select, button, form")) return;
    const point = mapPoint(surface, event);
    store.addEntity({
      position: {
        x: point.x - CARD_ANCHOR_X,
        y: point.y - CARD_ANCHOR_Y,
      },
    });
  });

  root.addEventListener("keydown", (event) => {
    const palette = root.querySelector<HTMLElement>("[data-command-palette]");
    if (!palette || palette.hidden) return;
    if (event.key === "Escape") {
      event.preventDefault();
      closeCommandPalette(root);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return;
    const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-command-action]"));
    if (buttons.length === 0) return;
    const activeIndex = buttons.findIndex((button) => button === document.activeElement);
    if (event.key === "Enter" && document.activeElement?.matches("[data-command-search]")) {
      event.preventDefault();
      buttons[Math.max(0, activeIndex)]?.click();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = activeIndex < 0 ? 0 : (activeIndex + direction + buttons.length) % buttons.length;
      buttons[nextIndex]?.focus();
    }
  });

  root.addEventListener("pointerdown", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("[data-delete-entity], input, textarea, select, button")) return;
    const card = target.closest<HTMLElement>("[data-card-id]");
    const surface = root.querySelector<HTMLElement>("[data-map-surface]");
    const activeTool = getActiveTool(root);
    if (!card) {
      const panSurface = target.closest<HTMLElement>("[data-map-surface]");
      if (panSurface && !target.closest("[data-relation-id], form")) {
        startPan(root, panSurface, event);
      }
      return;
    }
    const id = card.dataset.cardId;
    if (!id) return;
    if (target.closest("[data-connect-handle]") || activeTool === "connector") {
      event.preventDefault();
      event.stopPropagation();
      startConnect(root, store, surface, id, event);
    } else if (activeTool === "hand") {
      const panSurface = target.closest<HTMLElement>("[data-map-surface]");
      if (panSurface) startPan(root, panSurface, event);
    } else {
      startMove(root, store, card, id, event);
    }
  });

    window.addEventListener("keydown", (event) => {
    const key = event.key.toLowerCase();
    const mod = event.metaKey || event.ctrlKey;
    if (event.key === "Escape" && !isTextEditingActive()) {
      if (closeShareModal(root)) return;
      if (closeWorkspaceImportModal(root)) {
        pendingWorkspaceImport = null;
        return;
      }
      if (closeChatPanel(root)) return;
      if (closeToolPanels(root)) return;
      const state = store.getState();
      if (state.workspace.selectedEntityId || state.workspace.selectedRelationId) {
        store.select(null);
        return;
      }
      setActiveTool(root, "select");
      return;
    }
    if (mod && key === "k") {
      event.preventDefault();
      openCommandPalette(root, store);
      return;
    }
    if (mod && key === "z" && !isTextEditingActive()) {
      event.preventDefault();
      if (event.shiftKey) {
        store.redo();
      } else {
        store.undo();
      }
    }
  });
};

const createWorkspace = (): void => {
  window.location.href = workspaceUrlForRoom(createWorkspaceRoomId());
};

const renameCurrentWorkspace = (store: SyncrelStore, refreshRooms: () => void): void => {
  const state = store.getState();
  const nextName = window.prompt("Rename workspace", state.workspace.name);
  if (nextName === null) return;
  const cleanName = nextName.trim();
  if (!cleanName) return;
  store.renameWorkspace(cleanName);
  refreshRooms();
};

const switchWorkspace = (room: string): void => {
  if (!isValidWorkspaceRoom(room)) return;
  window.location.href = workspaceUrlForRoom(room);
};

const deleteCurrentWorkspace = async (store: SyncrelStore, refreshRooms: () => void): Promise<void> => {
  const room = store.getState().workspace.room;
  const confirmed = window.confirm(`Delete room "${room}" from this device?`);
  if (!confirmed) return;
  store.destroy();
  await deleteWorkspaceRoom(room);
  const remaining = (await listWorkspaceRooms()).filter((item) => item.room !== room);
  refreshRooms();
  if (remaining[0]) {
    switchWorkspace(remaining[0].room);
  } else {
    createWorkspace();
  }
};

type WorkspaceImportMode = "append" | "new";

const exportCurrentWorkspace = (root: HTMLElement, store: SyncrelStore): void => {
  const state = store.getState();
  const exportedAt = Date.now();
  downloadTextFile(
    workspaceFileName(state, exportedAt),
    exportWorkspaceFile(state, exportedAt),
    "application/json",
  );
  showShareToast(root, "Workspace file downloaded");
};

const handleWorkspaceImportFile = async (
  root: HTMLElement,
  input: HTMLInputElement,
  setPendingFile: (file: WorkspaceExportFile) => void,
): Promise<void> => {
  const file = input.files?.[0];
  if (!file) return;
  try {
    const workspaceFile = parseWorkspaceFile(await file.text());
    setPendingFile(workspaceFile);
    openWorkspaceImportModal(root, workspaceFile);
  } catch {
    closeWorkspaceImportModal(root);
    showShareToast(root, "Import failed: choose a Syncrel workspace file");
  } finally {
    input.value = "";
  }
};

const applyWorkspaceImportChoice = async (
  root: HTMLElement,
  store: SyncrelStore,
  mode: WorkspaceImportMode,
  pendingFile: WorkspaceExportFile | null,
  refreshRooms: () => void,
): Promise<void> => {
  if (!pendingFile) {
    closeWorkspaceImportModal(root);
    showShareToast(root, "Choose a workspace file first");
    return;
  }

  if (mode === "append") {
    store.setState(appendWorkspaceImport(store.getState(), pendingFile.state));
    closeWorkspaceImportModal(root);
    refreshRooms();
    showShareToast(root, "Workspace appended");
    return;
  }

  const currentRoom = store.getState().workspace.room;
  const rooms = await listWorkspaceRooms();
  const room = importedWorkspaceRoom(
    pendingFile.state,
    [currentRoom, ...rooms.map((record) => record.room)],
  );
  await saveSnapshot(room, prepareImportedWorkspaceState(pendingFile.state, room));
  closeWorkspaceImportModal(root);
  showShareToast(root, "Imported as new workspace");
  window.setTimeout(() => switchWorkspace(room), 220);
};

const openWorkspaceImportModal = (root: HTMLElement, file: WorkspaceExportFile): void => {
  const modal = root.querySelector<HTMLElement>("[data-workspace-import-modal]");
  const summary = root.querySelector<HTMLElement>("[data-workspace-import-summary]");
  if (summary) {
    summary.textContent = workspaceImportSummary(file);
  }
  if (modal) modal.hidden = false;
};

const closeWorkspaceImportModal = (root: HTMLElement): boolean => {
  const modal = root.querySelector<HTMLElement>("[data-workspace-import-modal]");
  const wasOpen = Boolean(modal && !modal.hidden);
  if (modal) modal.hidden = true;
  return wasOpen;
};

const workspaceImportSummary = (file: WorkspaceExportFile): string => {
  const { state } = file;
  return `${state.workspace.name} (${file.sourceRoom}) - ${plural(state.entities.length, "card")}, ${plural(state.relations.length, "link")}, ${plural(state.schema.fields.length, "field")}`;
};

const plural = (count: number, label: string): string => (
  `${count} ${label}${count === 1 ? "" : "s"}`
);

const downloadTextFile = (filename: string, text: string, mimeType: string): void => {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const workspaceUrlForRoom = (room: string, href = window.location.href): string => {
  const url = new URL(href);
  url.searchParams.set("room", room);
  url.searchParams.delete("access");
  url.searchParams.delete("share");
  return url.toString();
};

const getAccessMode = (root: HTMLElement): ShareAccess => (
  root.dataset.accessMode === "view" ? "view" : "edit"
);

const isValidWorkspaceRoom = (room: string): boolean => /^[a-zA-Z0-9_-]{1,64}$/.test(room);

const openShareModal = (root: HTMLElement, store: SyncrelStore): void => {
  populateShareLinks(root, store);
  const modal = root.querySelector<HTMLElement>("[data-share-modal]");
  if (!modal) return;
  modal.hidden = false;
  const focusSelector = getAccessMode(root) === "view" ? "[data-share-view-link]" : "[data-share-edit-link]";
  window.setTimeout(() => root.querySelector<HTMLInputElement>(focusSelector)?.select(), 0);
};

const closeShareModal = (root: HTMLElement): boolean => {
  const modal = root.querySelector<HTMLElement>("[data-share-modal]");
  const wasOpen = Boolean(modal && !modal.hidden);
  if (modal) modal.hidden = true;
  return wasOpen;
};

const populateShareLinks = (root: HTMLElement, store: SyncrelStore): void => {
  const workspace = store.getState().workspace;
  const keys = workspace.share;
  const accessMode = getAccessMode(root);
  const editLink = workspaceShareLink(workspace.room, "edit", window.location.href, keys);
  const viewLink = workspaceShareLink(workspace.room, "view", window.location.href, keys);
  const editRow = root.querySelector<HTMLElement>("[data-share-edit-row]");
  const editInput = root.querySelector<HTMLInputElement>("[data-share-edit-link]");
  const viewInput = root.querySelector<HTMLInputElement>("[data-share-view-link]");
  if (editRow) editRow.hidden = accessMode === "view";
  if (editInput) {
    if (accessMode === "view") editInput.value = "";
    else editInput.value = editLink;
  }
  if (viewInput) viewInput.value = viewLink;
};

const copyShareLink = (root: HTMLElement, store: SyncrelStore, access: ShareAccess): void => {
  if (access === "edit" && getAccessMode(root) === "view") {
    showShareToast(root, "View-only users cannot copy edit links");
    return;
  }
  populateShareLinks(root, store);
  const selector = access === "view" ? "[data-share-view-link]" : "[data-share-edit-link]";
  const input = root.querySelector<HTMLInputElement>(selector);
  const workspace = store.getState().workspace;
  const link = input?.value || workspaceShareLink(workspace.room, access, window.location.href, workspace.share);
  input?.select();
  const write = navigator.clipboard?.writeText(link);
  if (write) {
    void write.then(
      () => showShareToast(root, `${access === "view" ? "View-only" : "Edit"} link copied`),
      () => showShareToast(root, "Copy failed"),
    );
  } else {
    showShareToast(root, `${access === "view" ? "View-only" : "Edit"} link ready`);
  }
};

let shareToastTimer: number | null = null;

const showShareToast = (root: HTMLElement, message: string): void => {
  const toast = root.querySelector<HTMLElement>("[data-share-toast]");
  if (!toast) return;
  toast.textContent = message;
  toast.hidden = false;
  if (shareToastTimer) window.clearTimeout(shareToastTimer);
  shareToastTimer = window.setTimeout(() => {
    toast.hidden = true;
  }, 1800);
};

const toggleChatPanel = (root: HTMLElement): void => {
  if (root.dataset.chatOpen === "true") {
    closeChatPanel(root);
  } else {
    openChatPanel(root);
  }
};

const openChatPanel = (root: HTMLElement): void => {
  root.dataset.chatOpen = "true";
  applyChatPanelState(root);
  window.setTimeout(() => focusChatInput(root), 0);
};

const closeChatPanel = (root: HTMLElement): boolean => {
  const wasOpen = root.dataset.chatOpen === "true";
  delete root.dataset.chatOpen;
  applyChatPanelState(root);
  return wasOpen;
};

const applyChatPanelState = (root: HTMLElement): void => {
  const open = root.dataset.chatOpen === "true";
  const panel = root.querySelector<HTMLElement>("[data-chat-panel]");
  if (panel) panel.hidden = !open;
  root.querySelectorAll<HTMLButtonElement>("[data-toggle-chat]").forEach((button) => {
    button.classList.toggle("is-active", open);
    button.setAttribute("aria-expanded", open ? "true" : "false");
  });
};

const focusChatInput = (root: HTMLElement): void => {
  root.querySelector<HTMLInputElement>("[data-chat-input]")?.focus();
};

const chatAuthorName = (): string => {
  const existing = localStorage.getItem(CHAT_AUTHOR_KEY);
  if (existing) return existing;
  const name = `Guest ${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
  localStorage.setItem(CHAT_AUTHOR_KEY, name);
  return name;
};

const specViewerId = (): string => {
  const existing = localStorage.getItem(SPEC_VIEWER_KEY);
  if (existing) return existing;
  const randomId = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : createShareToken(22);
  const id = `viewer-${randomId}`;
  localStorage.setItem(SPEC_VIEWER_KEY, id);
  return id;
};

const toggleToolPanel = (root: HTMLElement, name: string): void => {
  setToolPanel(root, name);
};

const openToolPanel = (root: HTMLElement, name: string): void => {
  setToolPanel(root, name, true);
};

export const shouldKeepToolPanelOnCanvasClick = (openPanel: string | null | undefined): boolean => (
  openPanel === "spec" || openPanel === "grid"
);

export const emptyCanvasClickEffect = (
  openPanel: string | null | undefined,
  justPanned: boolean,
): { closePanels: boolean; clearSelection: boolean } => {
  if (justPanned) return { closePanels: false, clearSelection: false };
  return {
    closePanels: !shouldKeepToolPanelOnCanvasClick(openPanel),
    clearSelection: true,
  };
};

const getGridSort = (root: HTMLElement): GridSort | null => {
  const key = root.dataset.gridSortActiveKey;
  const direction = root.dataset.gridSortActiveDirection;
  if (!key || (direction !== "asc" && direction !== "desc")) return null;
  return { key, direction };
};

const setGridSort = (root: HTMLElement, key: string): void => {
  if (!key) return;
  const current = getGridSort(root);
  root.dataset.gridSortActiveKey = key;
  root.dataset.gridSortActiveDirection = current?.key === key && current.direction === "asc" ? "desc" : "asc";
};

const setToolPanel = (root: HTMLElement, name: string, forceOpen?: boolean): void => {
  const panel = root.querySelector<HTMLElement>(`[data-tool-panel="${cssEscape(name)}"]`);
  const shouldOpen = forceOpen ?? Boolean(panel?.hidden);
  root.dataset.openToolPanel = panel && shouldOpen ? name : "";
  applyToolPanelState(root);
};

const closeToolPanels = (root: HTMLElement): boolean => {
  const hadOpenPanel = Boolean(root.dataset.openToolPanel);
  setToolPanel(root, "", false);
  return hadOpenPanel;
};

const applyToolPanelState = (root: HTMLElement): void => {
  const openPanel = root.dataset.openToolPanel ?? "";
  root.querySelectorAll<HTMLElement>("[data-tool-panel]").forEach((item) => {
    item.hidden = item.dataset.toolPanel !== openPanel;
  });
  root.querySelectorAll<HTMLButtonElement>("[data-tool-toggle]").forEach((button) => {
    const active = Boolean(openPanel) && button.dataset.toolToggle === openPanel;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-expanded", active ? "true" : "false");
  });
};

const canvasToolValues: CanvasTool[] = ["select", "hand", "node", "connector", "note"];

const asCanvasTool = (value: string | undefined): CanvasTool => (
  canvasToolValues.includes(value as CanvasTool) ? value as CanvasTool : DEFAULT_CANVAS_TOOL
);

const getActiveTool = (root: HTMLElement): CanvasTool => asCanvasTool(root.dataset.activeTool);

const setActiveTool = (root: HTMLElement, tool: CanvasTool): void => {
  root.dataset.activeTool = tool;
  updateToolModeButtons(root, tool);
  applyCanvasTool(root, tool);
};

const updateToolModeButtons = (root: HTMLElement, tool: CanvasTool): void => {
  root.querySelectorAll<HTMLButtonElement>("[data-tool-mode]").forEach((button) => {
    const active = button.dataset.toolMode === tool;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-pressed", active ? "true" : "false");
  });
};

const applyCanvasTool = (root: HTMLElement, tool: CanvasTool): void => {
  const surface = root.querySelector<HTMLElement>("[data-map-surface]");
  if (!surface) return;
  surface.dataset.activeTool = tool;
};

const fieldTypeValues: FieldType[] = [
  "text",
  "number",
  "currency",
  "percent",
  "select",
  "multi-select",
  "boolean",
  "computed",
];

const asFieldType = (value: string): FieldType => (
  fieldTypeValues.includes(value as FieldType) ? value as FieldType : "text"
);

const createEntityAt = (
  root: HTMLElement,
  store: SyncrelStore,
  position: { x: number; y: number },
  tool: CanvasTool,
): void => {
  if (tool === "note") {
    store.addEntity({
      type: "note",
      name: "New note",
      status: "active",
      doc: "# New note\n\n",
      position,
    });
  } else {
    store.addEntity({
      type: "feature",
      name: "New feature",
      position,
    });
  }
  focusSelectedEntity(root, store);
};

const createLinkedEntity = (
  root: HTMLElement,
  store: SyncrelStore,
  sourceId: string,
  direction: string,
): void => {
  const source = store.getState().entities.find((entity) => entity.id === sourceId);
  if (!source) return;
  const offset = direction === "bottom" ? { x: 0, y: 142 } : { x: 238, y: 0 };
  store.addEntity({
    type: "feature",
    name: "Linked feature",
    position: {
      x: source.position.x + offset.x,
      y: source.position.y + offset.y,
    },
  });
  const createdId = store.getState().workspace.selectedEntityId;
  if (createdId) {
    store.linkEntities({
      fromId: sourceId,
      toId: createdId,
      type: "drives",
      label: "drives",
    });
  }
  focusSelectedEntity(root, store);
};

const focusSelectedEntity = (root: HTMLElement, store: SyncrelStore): void => {
  const id = store.getState().workspace.selectedEntityId;
  if (!id) return;
  focusEntityName(root, id);
};

const focusEntityName = (root: HTMLElement, id: string): void => {
  window.setTimeout(() => {
    const selector = `[data-inline-title="${cssEscape(id)}"], [data-inspector-name="${cssEscape(id)}"]`;
    const input = root.querySelector<HTMLInputElement>(selector);
    input?.focus();
    input?.select();
  }, 0);
};

const focusSpecEditor = (root: HTMLElement, id?: string): void => {
  window.setTimeout(() => {
    const selector = id
      ? `[data-spec-doc="${cssEscape(id)}"]`
      : "[data-tool-panel=\"spec\"] [data-spec-doc]";
    const textarea = root.querySelector<HTMLTextAreaElement>(selector);
    textarea?.focus();
  }, 0);
};

const isSpecOutlineExpanded = (root: HTMLElement): boolean => (
  root.dataset.specOutlineExpanded === "true"
);

const toggleSpecOutline = (root: HTMLElement): void => {
  const nextExpanded = !isSpecOutlineExpanded(root);
  if (nextExpanded) {
    root.dataset.specOutlineExpanded = "true";
  } else {
    delete root.dataset.specOutlineExpanded;
  }
  applySpecOutlineState(root, nextExpanded);
};

const applySpecOutlineState = (root: HTMLElement, expanded: boolean): void => {
  const workspace = root.querySelector<HTMLElement>('[data-tool-panel="spec"] .spec-workspace');
  const outline = root.querySelector<HTMLElement>('[data-tool-panel="spec"] [data-spec-outline-panel]');
  const toggle = root.querySelector<HTMLButtonElement>('[data-tool-panel="spec"] [data-toggle-spec-outline]');
  if (!workspace || !outline || !toggle) return;

  if (expanded) {
    workspace.dataset.specOutlineExpanded = "true";
    delete workspace.dataset.specOutlineCollapsed;
  } else {
    delete workspace.dataset.specOutlineExpanded;
    workspace.dataset.specOutlineCollapsed = "true";
  }

  outline.setAttribute("aria-hidden", expanded ? "false" : "true");
  toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
  toggle.setAttribute("title", expanded ? "Hide document outline" : "Show document outline");
  toggle.classList.toggle("is-active", expanded);
};

const insertSpecSnippet = (root: HTMLElement, store: SyncrelStore, kind: string): void => {
  const textarea = root.querySelector<HTMLTextAreaElement>("[data-tool-panel=\"spec\"] [data-spec-doc]");
  if (!textarea) return;
  const id = textarea.dataset.specDoc;
  if (!id) return;
  const snippet = specSnippet(kind);
  const start = textarea.selectionStart ?? textarea.value.length;
  const end = textarea.selectionEnd ?? textarea.value.length;
  const prefix = start > 0 && !textarea.value.slice(0, start).endsWith("\n") ? "\n\n" : "";
  const suffix = end < textarea.value.length && !textarea.value.slice(end).startsWith("\n") ? "\n\n" : "";
  const next = `${textarea.value.slice(0, start)}${prefix}${snippet}${suffix}${textarea.value.slice(end)}`;
  textarea.value = next;
  const cursor = start + prefix.length + snippet.length;
  textarea.setSelectionRange(cursor, cursor);
  textarea.focus();
  store.patchEntity(id, { doc: next });
};

const specSnippet = (kind: string): string => {
  if (kind === "decision") {
    return "## Decision\n- Decision:\n- Why now:\n- Tradeoffs:\n";
  }
  if (kind === "question") {
    return "## Open Question\n- Question:\n- Owner:\n- Needed by:\n";
  }
  if (kind === "bullet") {
    return "- ";
  }
  return "## Section\n";
};

const cssEscape = (value: string): string => (
  typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(value)
    : value.replace(/["\\]/g, "\\$&")
);

const getMapZoom = (root: HTMLElement): number => {
  const value = Number(root.dataset.mapZoom ?? 1);
  return Number.isFinite(value) ? clampZoom(value) : 1;
};

const zoomBy = (root: HTMLElement, delta: number): void => {
  setMapZoom(root, getMapZoom(root) + delta);
};

const setMapZoom = (
  root: HTMLElement,
  value: number,
  anchor?: { clientX: number; clientY: number },
): void => {
  const surface = root.querySelector<HTMLElement>("[data-map-surface]");
  const current = getMapZoom(root);
  const next = clampZoom(value);
  if (!surface) {
    root.dataset.mapZoom = String(next);
    updateZoomControls(root, next);
    return;
  }

  const rect = surface.getBoundingClientRect();
  const anchorClientX = anchor?.clientX ?? rect.left + rect.width / 2;
  const anchorClientY = anchor?.clientY ?? rect.top + rect.height / 2;
  const anchorX = anchorClientX - rect.left;
  const anchorY = anchorClientY - rect.top;
  const size = surfaceViewportSize(surface);
  const camera = getMapCamera(root);
  const worldX = camera.x + (anchorX - size.width / 2) / current;
  const worldY = camera.y + (anchorY - size.height / 2) / current;

  root.dataset.mapZoom = String(next);
  writeMapCamera(root, {
    x: worldX - (anchorX - size.width / 2) / next,
    y: worldY - (anchorY - size.height / 2) / next,
  });
  applyMapZoom(root, next);
};

const applyMapZoom = (root: HTMLElement, zoom: number): void => {
  const surface = root.querySelector<HTMLElement>("[data-map-surface]");
  const frame = root.querySelector<HTMLElement>("[data-map-world-frame]");
  const world = root.querySelector<HTMLElement>("[data-map-world]");
  const next = clampZoom(zoom);
  const camera = getMapCamera(root);
  root.dataset.mapZoom = String(next);
  writeMapCamera(root, camera);
  if (surface) {
    const size = surfaceViewportSize(surface);
    const offsetX = size.width / 2 - camera.x * next;
    const offsetY = size.height / 2 - camera.y * next;
    const gridSize = Math.max(4, MAP_GRID_BASE_SIZE * next);
    surface.dataset.zoom = String(next);
    surface.dataset.cameraX = formatCameraValue(camera.x);
    surface.dataset.cameraY = formatCameraValue(camera.y);
    surface.style.setProperty("--map-zoom", String(next));
    surface.style.setProperty("--map-grid-size", `${gridSize}px`);
    surface.style.setProperty("--map-grid-x", `${positiveModulo(offsetX, gridSize)}px`);
    surface.style.setProperty("--map-grid-y", `${positiveModulo(offsetY, gridSize)}px`);
    if (world) {
      applyCrispWorldZoom(world, offsetX, offsetY, next);
    }
  }
  if (frame) {
    frame.style.width = "";
    frame.style.height = "";
  }
  updateZoomControls(root, next);
};

const applyCrispWorldZoom = (world: HTMLElement, offsetX: number, offsetY: number, zoom: number): void => {
  const snappedX = snapScreenPixel(offsetX);
  const snappedY = snapScreenPixel(offsetY);

  if (supportsLayoutZoom()) {
    world.style.left = `${snappedX / zoom}px`;
    world.style.top = `${snappedY / zoom}px`;
    world.style.transform = "none";
    world.style.setProperty("zoom", String(zoom));
    return;
  }

  world.style.left = "0px";
  world.style.top = "0px";
  world.style.removeProperty("zoom");
  world.style.transform = `translate(${snappedX}px, ${snappedY}px) scale(${zoom})`;
};

const supportsLayoutZoom = (): boolean => (
  typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("zoom", "1")
);

const snapScreenPixel = (value: number): number => {
  const ratio = window.devicePixelRatio || 1;
  return Math.round(value * ratio) / ratio;
};

const updateZoomControls = (root: HTMLElement, zoom: number): void => {
  const readout = root.querySelector<HTMLElement>("[data-zoom-value]");
  if (readout) readout.textContent = `${Math.round(zoom * 100)}%`;
  root.querySelectorAll<HTMLButtonElement>("[data-zoom-out]").forEach((button) => {
    button.disabled = zoom <= MAP_ZOOM_MIN + 0.001;
  });
  root.querySelectorAll<HTMLButtonElement>("[data-zoom-in]").forEach((button) => {
    button.disabled = zoom >= MAP_ZOOM_MAX - 0.001;
  });
};

const clampZoom = (value: number): number => (
  Math.min(MAP_ZOOM_MAX, Math.max(MAP_ZOOM_MIN, Math.round(value * 100) / 100))
);

const mapPoint = (surface: HTMLElement, event: { clientX: number; clientY: number }): { x: number; y: number } => {
  const rect = surface.getBoundingClientRect();
  const zoom = readSurfaceZoom(surface);
  const camera = readSurfaceCamera(surface);
  const size = surfaceViewportSize(surface);
  return {
    x: camera.x + (event.clientX - rect.left - size.width / 2) / zoom,
    y: camera.y + (event.clientY - rect.top - size.height / 2) / zoom,
  };
};

const readSurfaceZoom = (surface: HTMLElement): number => {
  const value = Number(surface.dataset.zoom ?? 1);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

interface MapCamera {
  x: number;
  y: number;
}

const defaultMapCamera = (): MapCamera => ({
  x: MAP_WORLD_WIDTH / 2,
  y: MAP_WORLD_HEIGHT / 2,
});

const getMapCamera = (root: HTMLElement): MapCamera => {
  const fallback = defaultMapCamera();
  const x = Number(root.dataset.mapCameraX);
  const y = Number(root.dataset.mapCameraY);
  return {
    x: Number.isFinite(x) ? x : fallback.x,
    y: Number.isFinite(y) ? y : fallback.y,
  };
};

const writeMapCamera = (root: HTMLElement, camera: MapCamera): void => {
  root.dataset.mapCameraX = formatCameraValue(camera.x);
  root.dataset.mapCameraY = formatCameraValue(camera.y);
  const surface = root.querySelector<HTMLElement>("[data-map-surface]");
  if (surface) {
    surface.dataset.cameraX = formatCameraValue(camera.x);
    surface.dataset.cameraY = formatCameraValue(camera.y);
  }
};

const setMapCamera = (root: HTMLElement, camera: MapCamera): void => {
  writeMapCamera(root, camera);
  applyMapZoom(root, getMapZoom(root));
};

const readSurfaceCamera = (surface: HTMLElement): MapCamera => {
  const fallback = defaultMapCamera();
  const x = Number(surface.dataset.cameraX);
  const y = Number(surface.dataset.cameraY);
  return {
    x: Number.isFinite(x) ? x : fallback.x,
    y: Number.isFinite(y) ? y : fallback.y,
  };
};

const surfaceViewportSize = (surface: HTMLElement): { width: number; height: number } => {
  const rect = surface.getBoundingClientRect();
  return {
    width: rect.width || surface.clientWidth || 1,
    height: rect.height || surface.clientHeight || 1,
  };
};

const positiveModulo = (value: number, divisor: number): number => (
  ((value % divisor) + divisor) % divisor
);

const formatCameraValue = (value: number): string => (
  String(Math.round(value * 1000) / 1000)
);

interface TutorialStep {
  target: string;
  title: string;
  body: string;
}

interface TutorialRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

interface TutorialBox {
  width: number;
  height: number;
}

const clampNumber = (value: number, min: number, max: number): number => (
  Math.min(Math.max(value, min), Math.max(min, max))
);

export const tutorialCardPosition = (
  target: TutorialRect,
  card: TutorialBox,
  viewport: TutorialBox,
  margin = 12,
  gap = 14,
): { left: number; top: number } => {
  const maxLeft = Math.max(margin, viewport.width - card.width - margin);
  const targetIsWide = target.width >= card.width + margin * 2;
  const preferredLeft = targetIsWide
    ? target.left + gap
    : target.left + target.width / 2 - card.width / 2;
  const left = clampNumber(preferredLeft, margin, maxLeft);

  const maxTop = Math.max(margin, viewport.height - card.height - margin);
  const below = target.bottom + gap;
  const above = target.top - card.height - gap;
  const hasBelow = below <= maxTop;
  const hasAbove = above >= margin;

  let preferredTop: number;
  if (hasBelow && target.height < viewport.height * 0.55) {
    preferredTop = below;
  } else if (hasAbove) {
    preferredTop = above;
  } else if (hasBelow) {
    preferredTop = below;
  } else if (target.height > card.height + gap * 2) {
    preferredTop = target.top + gap;
  } else {
    preferredTop = target.top + target.height / 2 - card.height / 2;
  }

  return {
    left,
    top: clampNumber(preferredTop, margin, maxTop),
  };
};

const tutorialSteps: TutorialStep[] = [
  {
    target: "[data-new-workspace]",
    title: "Start with a workspace",
    body: "Create a new room when you want a fresh product strategy board. The room link is the workspace.",
  },
  {
    target: "[data-tool-toggle='frameworks']",
    title: "Choose framework tabs",
    body: "Open Frameworks to activate the tabs you want and add more methods from the predefined library.",
  },
  {
    target: "[data-add-entity]",
    title: "Create canvas nodes",
    body: "Use the toolbar node button to add a new idea, then arrange it directly on the canvas.",
  },
  {
    target: "[data-map-surface]",
    title: "Ideate on the canvas",
    body: "Double-click empty canvas space to create a feature. Drag cards to arrange ideas and drag the plus handle to connect them.",
  },
  {
    target: "[data-tool-toggle='grid']",
    title: "Open structured fields",
    body: "Fields, specs, and settings expand from this toolbar while the canvas stays visible.",
  },
  {
    target: "[data-tool-toggle='spec']",
    title: "Add spec polls",
    body: "Open Spec to write the doc and add inline polls that readers can vote on after reading.",
  },
  {
    target: "[data-tool-toggle='settings']",
    title: "Reveal data tools only when needed",
    body: "Imports and exports live in Settings & data so the normal workflow stays focused.",
  },
];

const openTutorial = (root: HTMLElement, stepIndex: number): void => {
  const layer = root.querySelector<HTMLElement>("[data-tutorial-layer]");
  const card = root.querySelector<HTMLElement>("[data-tutorial-card]");
  const spotlight = root.querySelector<HTMLElement>("[data-tutorial-spotlight]");
  if (!layer || !card || !spotlight) return;

  const bounded = Math.max(0, Math.min(stepIndex, tutorialSteps.length - 1));
  const step = tutorialSteps[bounded];
  layer.hidden = false;
  layer.dataset.tutorialStep = String(bounded);
  card.innerHTML = `
    <div class="tutorial-card__meta">Step ${bounded + 1} of ${tutorialSteps.length}</div>
    <h2>${escapeHtml(step.title)}</h2>
    <p>${escapeHtml(step.body)}</p>
    <div class="tutorial-card__actions">
      <button type="button" data-tutorial-action="close">Skip</button>
      <button type="button" data-tutorial-action="back" ${bounded === 0 ? "disabled" : ""}>Back</button>
      <button type="button" data-tutorial-action="${bounded === tutorialSteps.length - 1 ? "done" : "next"}">
        ${bounded === tutorialSteps.length - 1 ? "Finish" : "Next"}
      </button>
    </div>
  `;
  card.style.left = "0";
  card.style.top = "0";

  const target = root.querySelector<HTMLElement>(step.target);
  target?.scrollIntoView({ block: "nearest", inline: "nearest" });
  const rect = target?.getBoundingClientRect();
  const viewport = {
    width: window.innerWidth || document.documentElement.clientWidth || 1,
    height: window.innerHeight || document.documentElement.clientHeight || 1,
  };

  if (rect) {
    spotlight.hidden = false;
    const spotlightLeft = clampNumber(rect.left - 6, 4, viewport.width - 4);
    const spotlightTop = clampNumber(rect.top - 6, 4, viewport.height - 4);
    const spotlightRight = clampNumber(rect.right + 6, 4, viewport.width - 4);
    const spotlightBottom = clampNumber(rect.bottom + 6, 4, viewport.height - 4);
    spotlight.style.left = `${spotlightLeft}px`;
    spotlight.style.top = `${spotlightTop}px`;
    spotlight.style.width = `${Math.max(0, spotlightRight - spotlightLeft)}px`;
    spotlight.style.height = `${Math.max(0, spotlightBottom - spotlightTop)}px`;

    const cardPosition = tutorialCardPosition(
      rect,
      {
        width: card.offsetWidth || Math.min(368, viewport.width - 24),
        height: card.offsetHeight || 168,
      },
      viewport,
    );
    card.style.left = `${cardPosition.left}px`;
    card.style.top = `${cardPosition.top}px`;
  } else {
    spotlight.hidden = true;
    const fallbackPosition = tutorialCardPosition(
      { left: 64, top: 64, right: 64, bottom: 64, width: 0, height: 0 },
      {
        width: card.offsetWidth || Math.min(368, viewport.width - 24),
        height: card.offsetHeight || 168,
      },
      viewport,
    );
    card.style.left = `${fallbackPosition.left}px`;
    card.style.top = `${fallbackPosition.top}px`;
  }
};

const handleTutorialAction = (root: HTMLElement, action: string): void => {
  const layer = root.querySelector<HTMLElement>("[data-tutorial-layer]");
  const current = Number(layer?.dataset.tutorialStep ?? 0);
  if (action === "next") {
    openTutorial(root, current + 1);
  } else if (action === "back") {
    openTutorial(root, current - 1);
  } else {
    closeTutorial(root);
  }
};

const closeTutorial = (root: HTMLElement): void => {
  const layer = root.querySelector<HTMLElement>("[data-tutorial-layer]");
  if (layer) layer.hidden = true;
  localStorage.setItem(TUTORIAL_KEY, "1");
};

const nextEntityPosition = (state: SyncrelState): { x: number; y: number } => {
  const index = visibleFrameworkEntities(state).length;
  const column = index % 3;
  const row = Math.floor(index / 3) % 4;
  return {
    x: 830 + column * 54,
    y: 80 + row * 118,
  };
};

const openCommandPalette = (root: HTMLElement, store: SyncrelStore): void => {
  const palette = root.querySelector<HTMLElement>("[data-command-palette]");
  const input = root.querySelector<HTMLInputElement>("[data-command-search]");
  if (!palette || !input) return;
  palette.hidden = false;
  input.value = "";
  renderCommandItems(root, store, "");
  window.setTimeout(() => input.focus(), 0);
};

const closeCommandPalette = (root: HTMLElement): void => {
  const palette = root.querySelector<HTMLElement>("[data-command-palette]");
  if (palette) palette.hidden = true;
};

const renderCommandItems = (root: HTMLElement, store: SyncrelStore, query: string): void => {
  const list = root.querySelector<HTMLElement>("[data-command-list]");
  if (!list) return;
  const normalized = query.trim().toLowerCase();
  const items = commandItems(store.getState()).filter((item) => (
    !normalized ||
    item.label.toLowerCase().includes(normalized) ||
    item.meta.toLowerCase().includes(normalized)
  ));
  list.innerHTML = items.length
    ? items.slice(0, 12).map((item) => `
      <button type="button" data-command-action="${escapeHtml(item.action)}">
        <strong>${escapeHtml(item.label)}</strong>
        <span>${escapeHtml(item.meta)}</span>
      </button>
    `).join("")
    : `<div class="command-empty">No matches</div>`;
};

const commandItems = (state: SyncrelState): Array<{ action: string; label: string; meta: string }> => [
  { action: "create:feature", label: "Create feature", meta: "Canvas entity" },
  ...frameworkCatalog.map((framework) => ({
    action: `framework:${framework.id}`,
    label: `Run ${framework.shortLabel}`,
    meta: "Framework",
  })),
  ...visibleFrameworkEntities(state).map((entity) => ({
    action: `entity:${entity.id}`,
    label: entity.name,
    meta: `${entity.type} · ${entity.status}`,
  })),
];

const executeCommand = (root: HTMLElement, store: SyncrelStore, action: string): void => {
  if (action === "create:feature") {
    store.addEntity({ position: nextEntityPosition(store.getState()) });
  } else if (action.startsWith("entity:")) {
    store.select(action.slice("entity:".length));
  } else if (action.startsWith("framework:")) {
    const frameworkId = action.slice("framework:".length) as FrameworkId;
    prepareFrameworkWelcome(root, store, frameworkId);
    store.enableFramework(frameworkId);
  }
  closeCommandPalette(root);
};

const isTextEditingActive = (): boolean => {
  const active = document.activeElement;
  return active instanceof HTMLElement && active.matches("input, textarea, select, [contenteditable='true']");
};

const blurActiveTextControl = (): void => {
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    active.matches("input:not([type=checkbox]):not([type=radio]), textarea, [contenteditable='true']")
  ) {
    active.blur();
  }
};

const updateSpecPollOptionDisplay = (root: HTMLElement, optionId: string, label: string): void => {
  if (!optionId) return;
  const display = root.querySelector<HTMLElement>(`[data-spec-poll-option-display="${cssEscape(optionId)}"]`);
  if (display) display.textContent = label || "Untitled option";
};

const checkedGridEntityIds = (root: HTMLElement): string[] => (
  Array.from(root.querySelectorAll<HTMLInputElement>("[data-export-check]:checked")).map((input) => input.value)
);

const coerceValue = (value: string): string | number => {
  const trimmed = value.trim();
  if (trimmed !== "" && Number.isFinite(Number(trimmed))) return Number(trimmed);
  return value;
};

// ---------------------------------------------------------------------------
// Spatial map interactions: drag-to-arrange and drag-to-connect.
// ---------------------------------------------------------------------------

const startPan = (root: HTMLElement, surface: HTMLElement, event: PointerEvent): void => {
  const startX = event.clientX;
  const startY = event.clientY;
  const origin = getMapCamera(root);
  const zoom = getMapZoom(root);
  let moved = false;

  const move = (moveEvent: PointerEvent) => {
    const dx = moveEvent.clientX - startX;
    const dy = moveEvent.clientY - startY;
    if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    moved = true;
    surface.dataset.justPanned = "1";
    surface.classList.add("is-panning");
    setMapCamera(root, {
      x: origin.x - dx / zoom,
      y: origin.y - dy / zoom,
    });
    moveEvent.preventDefault();
  };

  const finish = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", finish);
    surface.classList.remove("is-panning");
    if (moved) window.setTimeout(() => {
      if (surface.dataset.justPanned === "1") delete surface.dataset.justPanned;
    }, 0);
  };

  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", finish);
  window.addEventListener("pointercancel", finish);
};

const startMove = (root: HTMLElement, store: SyncrelStore, card: HTMLElement, id: string, event: PointerEvent): void => {
  event.preventDefault();
  const startX = event.clientX;
  const startY = event.clientY;
  const entity = store.getState().entities.find((item) => item.id === id);
  if (!entity) return;
  const origin = { ...entity.position };
  const zoom = getMapZoom(root);
  let moved = false;

  try {
    card.setPointerCapture(event.pointerId);
  } catch {
    /* pointer capture is best-effort */
  }

  const move = (moveEvent: PointerEvent) => {
    const dx = (moveEvent.clientX - startX) / zoom;
    const dy = (moveEvent.clientY - startY) / zoom;
    if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    moved = true;
    card.classList.add("is-dragging");
    card.style.left = `${origin.x + dx}px`;
    card.style.top = `${origin.y + dy}px`;
  };

  const finish = (upEvent: PointerEvent) => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", finish);
    card.classList.remove("is-dragging");
    try {
      card.releasePointerCapture(upEvent.pointerId);
    } catch {
      /* ignore */
    }
    if (moved) {
      store.moveEntity(id, {
        x: origin.x + (upEvent.clientX - startX) / zoom,
        y: origin.y + (upEvent.clientY - startY) / zoom,
      });
    } else if (id !== store.getState().workspace.selectedEntityId) {
      store.select(id);
    }
  };

  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", finish);
  window.addEventListener("pointercancel", finish);
};

const startConnect = (
  root: HTMLElement,
  store: SyncrelStore,
  surface: HTMLElement | null,
  fromId: string,
  event: PointerEvent,
): void => {
  const svg = root.querySelector<SVGSVGElement>(".map-links");
  const entity = store.getState().entities.find((item) => item.id === fromId);
  if (!svg || !surface || !entity) return;

  const x1 = entity.position.x + CARD_ANCHOR_X;
  const y1 = entity.position.y + CARD_ANCHOR_Y;
  const line = document.createElementNS(SVG_NS, "line");
  line.setAttribute("class", "map-temp-link");
  line.setAttribute("x1", String(x1));
  line.setAttribute("y1", String(y1));
  line.setAttribute("x2", String(x1));
  line.setAttribute("y2", String(y1));
  svg.appendChild(line);

  let target: HTMLElement | null = null;

  const move = (moveEvent: PointerEvent) => {
    const point = mapPoint(surface, moveEvent);
    line.setAttribute("x2", String(point.x));
    line.setAttribute("y2", String(point.y));

    const under = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY);
    const overCard = under instanceof HTMLElement ? under.closest<HTMLElement>("[data-card-id]") : null;
    const next = overCard && overCard.dataset.cardId !== fromId ? overCard : null;
    if (next !== target) {
      target?.classList.remove("is-link-target");
      target = next;
      target?.classList.add("is-link-target");
    }
  };

  const finish = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", finish);
    line.remove();
    const toId = target?.dataset.cardId ?? null;
    target?.classList.remove("is-link-target");
    if (toId && toId !== fromId) {
      const type = root.querySelector<HTMLSelectElement>("[data-link-type]")?.value ?? "drives";
      store.linkEntities({
        fromId,
        toId,
        type,
        label: type === "blocked-by" ? "blocked by" : type,
      });
    }
  };

  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", finish);
  window.addEventListener("pointercancel", finish);
};
